// components/VisitTable.jsx
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { MessageSquareText, Pencil, Tags } from "lucide-react";

export default function VisitTable({ data, showCustomerMeta = false, onEditCustomer }) {
  const navigate = useNavigate();

  const customStyles = {
    rows: {
      style: {
        minHeight: "76px",
        fontSize: "14px",
        color: "#334155",
        borderBottomColor: "#f1f5f9",
      },
    },
    headRow: {
      style: {
        minHeight: "48px",
        backgroundColor: "#f8fafc",
        borderBottomColor: "#e2e8f0",
      },
    },
    headCells: {
      style: {
        fontSize: "12px",
        fontWeight: 600,
        color: "#64748b",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      },
    },
    pagination: { style: { borderTopColor: "#e2e8f0", color: "#64748b" } },
  };

  const columns = [
    {
      name: "รหัส",
      selector: (row) => row.ulucu_user_id,
      sortable: true,
      cell: (row) => (
        <span
          className="cursor-pointer rounded-lg bg-teal-50 px-2 py-1 font-mono text-xs font-semibold text-teal-700 transition hover:bg-teal-100"
          onClick={() =>
            row.visitor_type !== "unknown"
              ? navigate(`/member/${row.ulucu_user_id}`)
              : navigate(`/visit-log/${row.ulucu_user_id}`)
          }
        >
          #{String(row.ulucu_user_id || "-").slice(-6)}
        </span>
      ),
    },

    {
      name: "รูป",
      width: "90px",
      cell: (row) => {
        // ✅ เลือกรูป
        let imgSrc = "";
        const age = parseInt(row.age); // กันพลาด แปลงเป็นตัวเลขก่อน

        if (row.sex === "1") {
          // 👨 ผู้ชาย
          if (age >= 19 && age <= 25) {
            imgSrc = "/M-19-25.jpg";
          } else if (age >= 26 && age <= 35) {
            imgSrc = "/M-26-35.jpg";
          } else if (age >= 36 && age <= 45) {
            imgSrc = "/M-36-45.jpg";
          } else if (age >= 46) {
            imgSrc = "/M-46-60.jpg";
          } else {
            imgSrc = "/M-19-25.jpg";
          }
        } else {
          // 👩 ผู้หญิง
          if (age >= 19 && age <= 25) {
            imgSrc = "/FM-19-25.jpg";
          } else if (age >= 26 && age <= 35) {
            imgSrc = "/FM-26-35.jpg";
          } else if (age >= 36 && age <= 45) {
            imgSrc = "/FM-36-45.jpg";
          } else if (age >= 46) {
            imgSrc = "/FM-36-45.jpg";
          } else {
            imgSrc = "/FM-19-25.jpg";
          }
        }

        imgSrc = row.arrive_image_url;

        return (
          <img
            src={imgSrc}
            className="h-14 w-14 rounded-xl border border-slate-200 object-cover shadow-sm"
            alt="รูปผู้เข้าใช้บริการ"
          />
        );
      },
    },

    {
      name: "เวลา",
      selector: (row) => row.arrive_time_thai,
      sortable: true,
    },

    {
      name: "เพศ",
      selector: (row) => (row.sex === "1" ? "ชาย" : "หญิง"),
      sortable: true,
      width: "80px",
    },

    {
      name: "อายุ",
      selector: (row) => row.age,
      sortable: true,
      right: true,
      width: "80px",
    },

    {
      name: "สถานะ",
      sortable: true,
      width: "130px",
      selector: (row) => row.visitor_type,
      cell: (row) => {
        const tones = {
          "ลูกค้า VIP": "bg-amber-50 text-amber-700 ring-amber-200",
          VIP: "bg-amber-50 text-amber-700 ring-amber-200",
          พนักงาน: "bg-teal-50 text-teal-700 ring-teal-200",
          "ลูกค้าทั่วไป": "bg-emerald-50 text-emerald-700 ring-emerald-200",
          unknown: "bg-slate-100 text-slate-600 ring-slate-200",
        };
        return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${tones[row.visitor_type] || tones.unknown}`}>{row.visitor_type === "unknown" ? "ไม่ทราบ" : row.visitor_type || "ไม่ทราบ"}</span>;
      },
    },

    // {
    //   name: "สถานะ",
    //   sortable: true,
    //   width: "100px",
    //   cell: row =>
    //     row.isvip === 1 ? (
    //       <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
    //         VIP
    //       </span>
    //     ) : (
    //       <span className="text-gray-400 text-xs">ทั่วไป</span>
    //     )
    // },

    {
      name: "สาขา",
      selector: (row) => row.device_name || "-",
      sortable: true,
      wrap: true,
    },
    ...(showCustomerMeta
      ? [
          {
            name: "Tag / Note",
            minWidth: "220px",
            cell: (row) => (
              <div className="min-w-0 py-2">
                {row.customer_tags?.length ? (
                  <div className="flex flex-wrap gap-1">
                    {row.customer_tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-cyan-50 px-2 py-0.5 text-[11px] font-medium text-cyan-700 ring-1 ring-cyan-100">
                        <Tags size={10} />{tag}
                      </span>
                    ))}
                    {row.customer_tags.length > 3 && <span className="text-[11px] text-slate-400">+{row.customer_tags.length - 3}</span>}
                  </div>
                ) : <span className="text-xs text-slate-300">ยังไม่มี Tag</span>}
                {row.customer_note ? (
                  <p className="mt-1.5 flex max-w-[210px] items-center gap-1 truncate text-xs text-slate-500" title={row.customer_note}><MessageSquareText size={11} className="shrink-0" />{row.customer_note}</p>
                ) : <p className="mt-1 text-[11px] text-slate-300">ยังไม่มี Note</p>}
              </div>
            ),
          },
          {
            name: "",
            width: "62px",
            cell: (row) => (
              <button onClick={() => onEditCustomer?.(row)} title="แก้ไข Tag และ Note" className="rounded-lg p-2 text-slate-400 transition hover:bg-cyan-50 hover:text-cyan-700">
                <Pencil size={15} />
              </button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="bg-white">
      <DataTable
        columns={columns}
        data={data}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 50]}
        highlightOnHover
        responsive
        persistTableHead
        noDataComponent="ไม่มีข้อมูล"
        customStyles={customStyles}
      />
    </div>
  );
}
